import React from 'react';
import { Box, Button, Chip, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import { useTranslation } from 'react-i18next';
import type { HseWeekExposureDraft, HseWeeklyReport } from '../../types/hse-weekly-report.type';
import { formatDateTime, isolate } from '../../utils/hseFormat';

export interface HseWeekStatusBarProps {
  report: HseWeeklyReport | null;
  exposure: HseWeekExposureDraft;
  onExposureChange: (field: keyof HseWeekExposureDraft, value: string) => void;
  readOnly: boolean;
  disabled?: boolean;
  onOpenHistory: () => void;
  onOpenWeeks: () => void;
}

/**
 * Week header: whether the week is still a draft or has been submitted (and by
 * whom), plus the exposure basis -- hours worked and headcount -- that injury
 * rates are calculated from.
 */
const HseWeekStatusBar: React.FC<HseWeekStatusBarProps> = ({
  report,
  exposure,
  onExposureChange,
  readOnly,
  disabled,
  onOpenHistory,
  onOpenWeeks,
}) => {
  const { t, i18n } = useTranslation();
  const submitted = report?.status === 'submitted';

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 2.5 },
        mb: 2,
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: '1 1 260px' }}>
        <Chip
          icon={submitted ? <LockOutlinedIcon /> : <EditNoteOutlinedIcon />}
          label={submitted ? t('hse.status.submitted') : t('hse.status.draft')}
          color="primary"
          variant={submitted ? 'filled' : 'outlined'}
          size="small"
          sx={{ fontWeight: 600 }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }} noWrap>
          {submitted
            ? t('hse.status.submittedBy', {
                name: isolate(report?.submitted_by_username ?? '—'),
                date: isolate(formatDateTime(report?.submitted_at, i18n.language)),
              })
            : report?.updated_at
              ? t('hse.status.lastSaved', { date: isolate(formatDateTime(report.updated_at, i18n.language)) })
              : t('hse.status.notSavedYet')}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        <TextField
          label={t('hse.exposure.hoursWorked')}
          type="number"
          size="small"
          value={exposure.hoursWorked}
          onChange={(e) => onExposureChange('hoursWorked', e.target.value)}
          disabled={disabled || readOnly}
          helperText={t('hse.exposure.hoursHelper')}
          inputProps={{ min: 0, step: 'any' }}
          InputProps={{ endAdornment: <InputAdornment position="end">{t('units.hours')}</InputAdornment> }}
          sx={{ width: 190 }}
        />
        <TextField
          label={t('hse.exposure.headcount')}
          type="number"
          size="small"
          value={exposure.headcount}
          onChange={(e) => onExposureChange('headcount', e.target.value)}
          disabled={disabled || readOnly}
          helperText={t('hse.exposure.headcountHelper')}
          inputProps={{ min: 0, step: 1 }}
          sx={{ width: 160 }}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 1, marginInlineStart: 'auto' }}>
        <Button variant="text" startIcon={<HistoryOutlinedIcon />} onClick={onOpenHistory} disabled={disabled}>
          {t('hse.history.button')}
        </Button>
        <Button variant="text" startIcon={<EventNoteOutlinedIcon />} onClick={onOpenWeeks} disabled={disabled}>
          {t('hse.weeks.button')}
        </Button>
      </Box>
    </Paper>
  );
};

export default HseWeekStatusBar;
